'use client'
import { memo, type RefObject, useCallback, useEffect, useRef, useState, type FC } from 'react'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import type { TypeCmsProjectInProduct } from '@/schema'
import { basicVariantsProps, designGrid, getP24_3, getTransition, mq, titleVariants, vw } from '@/styles'
import { useAnimation, useInView, motion } from 'motion/react'
import { TransitionSlider } from './TransitionSlider'

type Props = {
  data: TypeCmsProjectInProduct[]
  background?: boolean,
  ref?: RefObject<HTMLDivElement>
}

const changeColorVariants = {
  animate: parentState => ({
    color: parentState === 'full' ? '#FFF' : '#4D4D4D',
    transition: {
      ...getTransition(),
      duration: 1
    }
  })
}

const headingId = 'explore'
export const SectionExploreProjects: FC<Props> = memo(({ data, background = true, ref }) => {
  const [parentState, setParentState] = useState()
  const controls = useAnimation()
  // const isInView = useInView(ref, { margin: '40% 0% 0% 0%', once: false })
  const textRef = useRef(null)
  const isTextInView = useInView(textRef, { margin: '0px 0px -100px 0px' })
  const { t } = useTranslator()

  useEffect(() => {
    if(isTextInView) controls.start('animate')
  }, [controls, isTextInView])

  const wrapperSetParentState = useCallback(val => {
    setParentState(val)
  }, [setParentState])


  return (
    <Section ref={ref} aria-labelledby={headingId} className={parentState} $background={background}>
      <motion.div className='text-wrapper' ref={textRef} variants={titleVariants} {...basicVariantsProps} animate={controls}>
        <motion.h2 key={parentState} custom={parentState} variants={changeColorVariants} animate='animate' id={headingId} dangerouslySetInnerHTML={{ __html: t('project.product.section_explore.title') as string }}></motion.h2>
      </motion.div>
      <TransitionSlider data={data} parentState={parentState} parentStateSetter={wrapperSetParentState} />
    </Section>
  )
})

const Section = styled.section<{ $background: boolean }>`
  ${designGrid({})}
  background-color: ${({ $background }) => $background ? 'var(--color-creme)' : 'transparent'};
  overflow: hidden;
  padding: 90px 10px 40px;
  position: relative;
  

  ${mq.greaterThan('tablet')} {
    padding: 110px 0 40px;
  }

  .text-wrapper {
    grid-column: 1 / span 6;
    height: max-content;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 3;
    }

    h2 {
      ${getP24_3()}
      text-align: left;
      color: unset;
    }
  }

  figure {
    overflow: hidden;
    position: relative;
  }

  img {
    transition: 1000ms transform var(--transition-ease);
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }

  &.half {
    h2 { color: var(--color-white) }
    p { color: unset; }
    figure {
      margin: ${vw(132, 'mobile')} auto 0;

      /* WAS 150 */
      ${mq.greaterThan('tablet')} {
        margin: ${vw(120, 'desktop')} auto 0;
      }
    }
  }
`
